// Turns raw agent run errors + failure events into plain-English "what happened /
// how to fix" guidance so operators can self-serve. Shared by both agents.

// Run-level diagnosis from the run's `error` string + summary.
export function diagnoseRun(run, actions = []) {
  if (!run) return null;
  const err = (run.error || "").toLowerCase();
  const summary = run.summary || {};

  // Failed runs — map the error signature to actionable help.
  if (run.status === "failed") {
    if (err.includes("still on login page") || err.includes("login did not complete") || actions.some(a => a.event === "login_failed")) {
      return {
        tone: "error",
        title: "Gingr login failed",
        cause: "The Gingr username or password is incorrect, or the password was recently changed.",
        fix: "Open Setup below, re-enter the Gingr password and Save (this updates the login for every agent at this location), then run again.",
      };
    }
    if (err.includes("mfa") || err.includes("verification") || actions.some(a => a.event === "login_mfa_required")) {
      return {
        tone: "error",
        title: "Gingr requires 2-factor verification",
        cause: "The automation account has 2FA / a verification step enabled, which blocks automated login.",
        fix: "Use a Gingr login without 2FA for the agent, or ask Gingr to exempt this account from verification.",
      };
    }
    if (err.includes("chromium") || err.includes("browser") || actions.some(a => a.event === "browser_unavailable")) {
      return {
        tone: "warning",
        title: "Browser engine not ready",
        cause: "The automation browser was still installing — this happens for a minute right after a deploy.",
        fix: "Wait about a minute and run again. If it keeps happening, contact support.",
      };
    }
    if (err.includes("not configured") || err.includes("no agent settings") || err.includes("connection")) {
      return {
        tone: "error",
        title: "Agent not configured",
        cause: "This location has no Gingr login saved yet.",
        fix: "Fill in the Gingr URL, login email and password in Setup and click Save.",
      };
    }
    if (err.includes("cancelled")) {
      return {
        tone: "warning",
        title: "Run was force-stopped",
        cause: "This run was manually cancelled.",
        fix: "Start a new run whenever you're ready.",
      };
    }
    if (err.includes("deduped cross-pod")) {
      return {
        tone: "info",
        title: "Duplicate leftover run — safely cleaned up",
        cause: "Two schedulers briefly claimed today's slot at the same moment; this row is the auto-cleaned leftover, not a real failure.",
        fix: "No action needed — the other row on today's list is the successful run.",
      };
    }
    if (err.includes("auto-recovered") || err.includes("max runtime") || err.includes("worker") || err.includes("restart")) {
      return {
        tone: "info",
        title: "Leftover run auto-cleaned",
        cause: "The server was updated / restarted while this run was in flight, so we auto-cleaned this row. It's a housekeeping entry, not a fresh failure.",
        fix: "Nothing to do. If the day's send didn't happen, start a fresh run manually.",
      };
    }
    if (summary.permission_blocked) {
      return {
        tone: "error",
        title: "Missing Gingr permission",
        cause: "The automation login isn’t allowed to perform this action in Gingr.",
        fix: "In Gingr → Staff, grant this login permission to manage agreements/immunizations, then run again.",
      };
    }
    return {
      tone: "error",
      title: "Run failed",
      cause: run.error || "Something went wrong during the run.",
      fix: "Try again. If it repeats, open the audit screenshots below or contact support.",
    };
  }

  // Completed runs — surface soft issues.
  if (run.status === "completed") {
    if (summary.permission_blocked) {
      return {
        tone: "error",
        title: "Missing Gingr permission",
        cause: "The login opened the report but isn’t allowed to send from Gingr.",
        fix: "In Gingr → Staff, grant this login permission to manage agreements/immunizations.",
      };
    }
    const candidates = summary.customers_detected ?? summary.red_shields ?? summary.candidates ?? 0;
    if (candidates === 0) {
      return {
        tone: "info",
        title: "No matching customers found",
        cause: "Everyone with an upcoming reservation in your window is already signed / compliant — nothing to send.",
        fix: "That’s usually good news. To look further ahead, increase the “Look-ahead (days)” in Setup.",
      };
    }
    if ((summary.failed ?? 0) > 0) {
      return {
        tone: "warning",
        title: `${summary.failed} customer(s) couldn’t be actioned`,
        cause: "The run finished but some customers were skipped due to the reasons listed below.",
        fix: "Review each reason below — most are fixable in the customer’s Gingr profile.",
      };
    }
  }
  return null;
}

// Per-customer send failure reason -> friendly explanation.
export function explainFailure(reason) {
  const map = {
    // Unsigned Agreements
    no_phone_on_file: "Legacy row from an older build — the current agent always sends and trusts the report's phone filter. If new runs show this, you're on a stale deploy — hit Redeploy.",
    agreements_tab_missing: "Couldn't find the Agreements tab on the customer page — review the audit screenshot; Gingr's layout may have changed.",
    push_button_missing: "No “Push Agreements to Device” button — the customer may already be signed, or has no agreement to push.",
    signature_modal_missing: "The signature-capture screen didn’t appear after pushing — re-run; check the screenshot if it repeats.",
    send_sms_missing: "Gingr’s “Send SMS” button didn’t appear — usually a slow page load; re-run.",
    sms_dialog_missing: "The “Send Agreement Via SMS” dialog didn’t open — re-run; check the screenshot.",
    ok_button_missing: "Couldn’t confirm the SMS dialog — re-run; check the screenshot.",
    no_success_toast: "Send was clicked but Gingr’s “Agreement(s) sent to customer” confirmation didn’t appear within the wait window — check the audit screenshot; the SMS may still have gone out.",
    // Vaccine
    shield_click: "Couldn’t open the reminder for this row — re-run.",
    needs_confirmation_or_button_missing: "Gingr’s Email Reminder button/confirmation didn’t appear — re-run; check the screenshot.",
    // Shared
    permission_required: "The login lacks permission for this action — grant it in Gingr → Staff permissions.",
    duplicate_window: "Already contacted recently — skipped by your de-dup window.",
    skip_row_text: "Skipped by your “skip rows containing” rule.",
    exception: "Unexpected error on this customer — re-run; if it repeats, contact support.",
  };
  return map[reason] || reason || "Unknown issue.";
}

export const TONE_STYLES = {
  error: "border-rose-200 bg-rose-50 text-rose-800",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  info: "border-sky-200 bg-sky-50 text-sky-800",
};
